import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { getBooklets } from "../../services/bookletService";
import { fetchAllPages } from "../../utils/fetchAllPages";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { CHIP_TONES } from "../../components/QuestionList/FilterChips";
import { focusRing, linkButtonClass } from "../Exams/TestBuilder/ui";

// Shared pieces of the BTS booklet pages (/bts, /bts/new, /bts/:uuid).

export const BOOKLET_STATUSES = ["draft", "active", "archived"];
export const STATUS_TONES = { draft: CHIP_TONES.amber, active: CHIP_TONES.green, archived: CHIP_TONES.slate };
export const LANGUAGES = ["kk", "ru"];
export const GRADES = Array.from({ length: 12 }, (_, i) => i + 1);
export const DIGITS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// Look-alike Cyrillic capitals read as Latin ones, as the backend's folder_key does: «БТС-9», «BTS-9», «бтс-9».
const LOOKALIKES = { А: "A", В: "B", Е: "E", І: "I", К: "K", М: "M", Н: "H", О: "O", Р: "P", С: "C", Т: "T", У: "Y", Х: "X" };
const fold = (name) =>
  String(name || "")
    .toUpperCase()
    .replace(/[АВЕІКМНОРСТУХ]/g, (letter) => LOOKALIKES[letter])
    .toLowerCase();

// The BTS number (1–9) of a programme folder named «БТС-N» or «BTS-N» as a whole name, else null
// («BTS-1 2026 PreTest» is not BTS-1).
export function btsRound(name) {
  const match = /^(?:бtc|bts)-([1-9])$/.exec(fold(name));
  return match ? Number(match[1]) : null;
}

// The BTS programme folders of GET question_folders/ (top-level, named «БТС-N»), by their number.
export const btsFolders = (list) =>
  (list || [])
    .filter((folder) => !folder.parent && btsRound(folder.name) !== null)
    .sort((a, b) => btsRound(a.name) - btsRound(b.name));

// Every exam folder a booklet can be made of (BTS, TAT, a pretest…): the BTS ones first by number, then by name.
// A folder without a BTS number takes the codes' first digit by hand.
export const bookletFolders = (list) => {
  const rank = (folder) => btsRound(folder.name) ?? 10;
  return (list || [])
    .filter((folder) => !folder.parent)
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
};

// As printed: the BTS number, the grade in two digits, the variant digit («9101»).
export const variantCode = (round, grade, digit) =>
  round && grade ? `${round}${String(grade).padStart(2, "0")}${digit}` : "";

export const bookletHref = (uuid) => `/bts/${uuid}`;
// The list as it was left (its filters live in the address): its links pass their search on as `listSearch`.
export const listHref = (state) =>
  `/bts${typeof state?.listSearch === "string" && state.listSearch.startsWith("?") ? state.listSearch : ""}`;
// A section's variant in the bank's variants tab.
export const variantHref = (folderUuid, variantUuid) =>
  `/questions?folder=${folderUuid}&tab=variant${variantUuid ? `&variant=${variantUuid}` : ""}`;

export const languageLabel = (t, language) => (LANGUAGES.includes(language) ? t(`bts.languages.${language}`) : language || "");

// "БТС-9 · 10 сынып · ҚАЗ"
export const bookletMeta = (t, booklet) =>
  [booklet?.folder?.name, booklet?.grade ? t("bts.grade_value", { grade: booklet.grade }) : "", languageLabel(t, booklet?.language)]
    .filter(Boolean)
    .join(" · ");

export const fieldClass =
  "block w-full rounded-lg border-slate-300 px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500";
export const invalidClass = "border-red-300 focus:border-red-500 focus:ring-red-500/15";
export const labelClass = "mb-1.5 block text-sm font-medium text-slate-700";
// Buttons are 44px high on phones; links and link-like buttons at least (their text may wrap).
export const tapClass = "max-sm:h-11";
export const linkTapClass = "max-sm:min-h-11";
export const backLinkClass = `inline-flex items-center gap-1.5 self-start rounded-lg px-1.5 py-1 text-sm font-medium text-slate-500 transition duration-200 hover:bg-slate-100 hover:text-slate-900 ${linkTapClass} ${focusRing}`;
export const sectionTitleClass = "text-base font-semibold text-slate-900";
export const footerClass =
  "-mx-6 -mb-6 mt-6 flex flex-col-reverse gap-2 rounded-b-2xl border-t border-slate-200 bg-slate-50/60 px-6 py-4 sm:flex-row sm:justify-end";

export function FieldError({ id, children }) {
  if (!children) return null;
  return (
    <p id={id} role="alert" className="mt-1.5 text-sm text-red-600">
      {children}
    </p>
  );
}

// An input's aria-describedby: the ids of what is shown under it (its hint, its error, a conflict note).
export const describedBy = (...ids) => ids.filter(Boolean).join(" ") || undefined;

/**
 * Under a field the user changed while someone else saved another value for it (409 booklet_changed, then read
 * again): that saved value, and a button that takes it. The user's own input stays in the field.
 */
export function ConflictNote({ id, value, onTake, disabled = false }) {
  const [t] = useTranslation();
  return (
    <p id={id} className="mt-1.5 flex items-start gap-2 text-sm text-amber-800">
      <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 break-words">
        {t("bts.conflict.changed", { value })}{" "}
        <button type="button" onClick={onTake} disabled={disabled} className={`${linkButtonClass} ${linkTapClass} -my-1 py-0.5`}>
          {t("bts.conflict.take")}
        </button>
      </span>
    </p>
  );
}

// Focuses the element `id`, or the first control inside it (a select's input, a digit's button); false when
// there is none to focus.
export function focusField(id) {
  const element = id ? document.getElementById(id) : null;
  if (!element) return false;
  const control = element.matches("input, textarea, select, button")
    ? element
    : element.querySelector("input:not(:disabled), textarea:not(:disabled), select:not(:disabled), button:not(:disabled)");
  const target = control || element;
  if (target.disabled) return false;
  target.focus();
  return document.activeElement === target;
}

/**
 * The focus to give once the page has rendered again: a button the request disabled while it ran (a disabled
 * button loses the focus), a field that appears. Call the returned function with the element's id, and the id
 * to focus when that one is gone.
 */
export function useFocusAfterRender() {
  const target = useRef(null);
  useEffect(() => {
    if (!target.current) return;
    const [id, fallback] = target.current;
    target.current = null;
    if (!focusField(id)) focusField(fallback);
  });
  return useCallback((id, fallback) => {
    target.current = [id, fallback];
  }, []);
}

export function BookletStatusBadge({ status, className = "" }) {
  const [t] = useTranslation();
  if (!status) return null;
  const tone = STATUS_TONES[status] || CHIP_TONES.slate;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${tone.badge} ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
      {BOOKLET_STATUSES.includes(status) ? t(`bts.status.${status}`) : status}
    </span>
  );
}

// Frozen: what the booklet prints and its answer orders were fixed when it was activated.
export function FrozenBadge() {
  const [t] = useTranslation();
  return (
    <span
      title={t("bts.frozen_hint")}
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-inset ring-sky-600/20"
    >
      <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
      {t("bts.frozen")}
    </span>
  );
}

export function Codes({ codes, taken = [], className = "" }) {
  if (!codes?.length) return null;
  return (
    <span className={`flex flex-wrap gap-1 ${className}`}>
      {codes.map((code) => (
        <span
          key={code}
          className={`rounded-md px-1.5 py-0.5 font-mono text-xs tabular-nums ring-1 ring-inset ${
            taken.includes(code) ? "bg-amber-50 text-amber-900 ring-amber-300" : "bg-slate-50 text-slate-700 ring-slate-200"
          }`}
        >
          {code}
        </span>
      ))}
    </span>
  );
}

// The texts of a booklet's errors: the archived booklet's own text, then errors.<code>, the server's detail, `fallbackKey`.
export const bookletErrorMessage = (t, err, fallbackKey) =>
  getApiErrorCode(err) === "exam_archived" ? t("bts.errors.archived") : getApiErrorMessage(t, err, fallbackKey);

// A 400's field errors ({field: ["..."]}) as {field: "text"}; `known` limits them to the form's fields.
export function fieldErrors(err, known) {
  const data = err?.response?.status === 400 ? err.response.data : null;
  if (!data || typeof data !== "object" || data.code) return {};
  const result = {};
  Object.entries(data).forEach(([field, value]) => {
    if (known && !known.includes(field)) return;
    const text = Array.isArray(value) ? value.filter((v) => typeof v === "string").join(" ") : typeof value === "string" ? value : "";
    if (text) result[field] = text;
  });
  return result;
}

/**
 * The codes other active booklets of the same BTS number and grade print (activation refuses them, as the
 * backend's check does): {byDigit: Map(digit → [{uuid, name}]), loading, ready}. `exclude` is the booklet itself.
 * `refreshKey` changing reads them again (another booklet activated or archived meanwhile); the marks shown stay
 * until the new list comes. `ready`: the list of this BTS number and grade has come.
 */
export function useTakenCodes({ round, grade, exclude, refreshKey }) {
  const [state, setState] = useState({ key: "", booklets: [], loading: false });
  const key = round && grade ? `${round}:${grade}` : "";

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    let active = true;
    setState((prev) => ({ ...prev, loading: true }));
    fetchAllPages((page) => getBooklets({ params: { ...page, grade, status: "active" }, signal: controller.signal }))
      .then((rows) => {
        if (active) setState({ key, booklets: Array.isArray(rows) ? rows : [], loading: false });
      })
      .catch(() => {
        // Only a hint: without it, the activation still says which codes are taken.
        if (active) setState({ key, booklets: [], loading: false });
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [key, grade, refreshKey]);

  const byDigit = useMemo(() => {
    const map = new Map();
    if (state.key !== key) return map;
    state.booklets
      .filter((booklet) => booklet.round_number === round && booklet.uuid !== exclude)
      .forEach((booklet) =>
        (booklet.variant_numbers || []).forEach((digit) => {
          map.set(digit, [...(map.get(digit) || []), { uuid: booklet.uuid, name: booklet.name }]);
        })
      );
    return map;
  }, [state, key, round, exclude]);

  return { byDigit, loading: state.loading, ready: !!key && state.key === key };
}

// A segmented choice of the booklet's stream.
export function LanguageChoice({ id, value, onChange, disabled = false }) {
  const [t] = useTranslation();
  return (
    <fieldset disabled={disabled}>
      <legend className={labelClass}>{t("bts.fields.language")}</legend>
      <div className="inline-flex w-full rounded-lg bg-white p-0.5 ring-1 ring-inset ring-slate-300 sm:w-auto">
        {LANGUAGES.map((language) => (
          <label
            key={language}
            className={`relative flex h-9 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-md px-4 text-sm font-medium transition duration-200 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-blue-600 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60 max-sm:h-11 sm:flex-none ${
              value === language ? "bg-slate-800 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            }`}
          >
            <input
              type="radio"
              name={id}
              value={language}
              checked={value === language}
              onChange={() => onChange(language)}
              className="sr-only"
            />
            {t(`bts.language_names.${language}`)}
          </label>
        ))}
      </div>
    </fieldset>
  );
}
